import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Field, FieldGroup, NumberInput, Select } from '~/components/Form';
import { useIntl } from '~/hooks/useIntl';
import { srOnlyStyle } from '~/styles/utilities.css';
import { clsx } from '~/styles/utils';

import { useFormWithLinesContext } from '../../FormLines.context';
import { useLineContext } from '../../SectionLines/SectionLines.context';

export const FormDiscount = () => {
  const {
    clearErrors,
    control,
    formState: { isSubmitted },
    watch,
  } = useFormWithLinesContext();
  const { isMobile, lineFieldName } = useLineContext();

  const watchDiscountType = watch(`${lineFieldName}.discountType`);
  const watchPrice = watch(`${lineFieldName}.price`);
  const watchQuantity = watch(`${lineFieldName}.quantity`);
  const watchTotal = watchPrice * watchQuantity;

  const { formatCurrency, formatPercentage } = useIntl();
  const { t } = useTranslation(['lines', 'validation']);

  if (watchDiscountType === null) return null;

  const discountTypeItems = [
    { label: t('lines:discountType.percentage'), value: 'percentage' as const },
    { label: t('lines:discountType.flatRate'), value: 'flatRate' as const },
  ];

  return (
    <FieldGroup.Root>
      <FieldGroup.Legend className={clsx(!isMobile && srOnlyStyle)}>{t('lines:discount.header')}</FieldGroup.Legend>

      <FieldGroup.Items>
        <Controller
          control={control}
          name={`${lineFieldName}.discountValue`}
          render={({ field: { name, onBlur, onChange, ref, value }, fieldState: { error, invalid, isDirty, isTouched } }) => (
            <Field.Root dirty={isDirty} grow invalid={invalid} name={name} touched={isTouched}>
              <NumberInput onBlur={onBlur} onValueChange={onChange} ref={ref} value={value} />

              {isMobile && <Field.Error match={!!error}>{error?.message}</Field.Error>}
            </Field.Root>
          )}
          rules={{
            required: { value: true, message: t('validation:required') },
            min: {
              value: watchDiscountType === 'percentage' ? 1 : 0.01,
              message: t('validation:min', {
                attribute: t('lines:discount.header'),
                min: watchDiscountType === 'percentage' ? formatPercentage(0.01) : formatCurrency(0.01),
              }),
            },
            max: {
              value: watchDiscountType === 'percentage' ? 100 : watchTotal,
              message:
                watchDiscountType === 'percentage'
                  ? t('validation:max', { attribute: t('lines:discount.header'), max: formatPercentage(1) })
                  : t('validation:maxDiscount'),
            },
          }}
        />

        <Controller
          control={control}
          name={`${lineFieldName}.discountType`}
          render={({ field: { name, onChange, ref, value } }) => (
            <Field.Root name={name}>
              <Select.Root
                items={discountTypeItems}
                onValueChange={(next) => {
                  if (!next) return;
                  onChange(next);
                  if (isSubmitted) clearErrors(`${lineFieldName}.discountValue`);
                }}
                value={value}
              >
                <Select.Button ref={ref} />
                <Select.List>
                  {discountTypeItems.map((item) => (
                    <Select.Item key={item.value} value={item.value}>
                      {item.label}
                    </Select.Item>
                  ))}
                </Select.List>
              </Select.Root>
            </Field.Root>
          )}
        />
      </FieldGroup.Items>
    </FieldGroup.Root>
  );
};
